import { useState } from "react"
import {
  MapPin, Phone, Mail, Clock, Send, CheckCircle2, MessageSquare,
  Building2, Headphones, AlertCircle,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input, Textarea } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Select } from "../../components/ui/Select"
import { Badge } from "../../components/ui/Badge"
import { Alert } from "../../components/ui/Alert"
import { SCHOOL } from "../../lib/constants"
import { api, ApiError } from "../../lib/api"
import { toast } from "sonner"

const contactCards = [
  {
    icon: MapPin,
    title: "Visit Us",
    lines: [SCHOOL.address, SCHOOL.location],
    accent: "bg-brand-700",
  },
  {
    icon: Phone,
    title: "Call Us",
    lines: [SCHOOL.phone, "Mon–Fri: 8am – 5pm WAT"],
    accent: "bg-gold-600",
  },
  {
    icon: Mail,
    title: "Email Us",
    lines: [SCHOOL.email, "admissions@ymscoe.edu.ng"],
    accent: "bg-brand-800",
  },
  {
    icon: Clock,
    title: "Office Hours",
    lines: ["Monday – Friday", "8:00 AM – 5:00 PM WAT"],
    accent: "bg-gold-700",
  },
]

const subjectOptions = [
  { value: "", label: "Select a subject..." },
  { value: "Admissions", label: "Admissions Enquiry" },
  { value: "Fees & Payments", label: "Fees & Payments" },
  { value: "Academic Programmes", label: "Academic Programmes" },
  { value: "Teaching Practice", label: "Teaching Practice" },
  { value: "Verification", label: "Document Verification" },
  { value: "General", label: "General Enquiry" },
]

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.")
      return
    }
    setSubmitting(true)
    try {
      await api.post("/api/public/contact", form)
      setSubmitted(true)
      toast.success("Message sent! We will respond within 48 hours.")
      setForm({ name: "", email: "", phone: "", subject: "", message: "" })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to send message"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Contact" }]}
        subtitle="Get In Touch"
        title="We'd Love to Hear From You"
        description="Whether you have a question about admissions, fees, programmes or document verification, our team is ready to help. Reach out through any of the channels below."
      />

      {/* Contact info cards */}
      <section className="py-16 -mt-8">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactCards.map(c => (
              <Card key={c.title} className="card-lift border-0 shadow-md text-center">
                <CardContent className="pt-6">
                  <div className={`w-14 h-14 rounded-xl ${c.accent} grid place-items-center mx-auto mb-4 shadow-md`}>
                    <c.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">{c.title}</h3>
                  <div className="text-sm text-ink-600 dark:text-ink-400 space-y-1">
                    {c.lines.map((l, i) => <p key={i}>{l}</p>)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                      <MessageSquare className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    </div>
                    <div>
                      <CardTitle>Send a Message</CardTitle>
                      <CardDescription>Fill the form and we will respond within 48 hours.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-10">
                      <CheckCircle2 className="w-16 h-16 text-brand-600 dark:text-brand-400 mx-auto mb-4" />
                      <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">Message Sent!</h3>
                      <p className="text-sm text-ink-600 dark:text-ink-400 mb-6 max-w-md mx-auto">
                        Thank you for reaching out. Our team will review your enquiry and get back to you shortly via email.
                      </p>
                      <Button onClick={() => setSubmitted(false)} variant="outline">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g. Aisha Bello"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            value={form.phone}
                            onChange={e => setForm({ ...form, phone: e.target.value })}
                            placeholder="+234 800 000 0000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="subject">Subject *</Label>
                          <Select
                            id="subject"
                            value={form.subject}
                            onChange={e => setForm({ ...form, subject: e.target.value })}
                            options={subjectOptions}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          rows={6}
                          value={form.message}
                          onChange={e => setForm({ ...form, message: e.target.value })}
                          placeholder="Type your message here..."
                          required
                        />
                      </div>
                      <Alert variant="info" className="text-xs">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>For document verification, please use the QR code on the document or visit our verification portal directly. Do not include sensitive data in this form.</span>
                        </div>
                      </Alert>
                      <Button type="submit" disabled={submitting} className="w-full" size="lg">
                        {submitting ? "Sending..." : (<><Send className="w-4 h-4 mr-2" />Send Message</>)}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-0 shadow-md bg-gradient-to-br from-brand-800 to-brand-950 text-white">
                <CardContent className="pt-6">
                  <Headphones className="w-10 h-10 text-gold-300 mb-3" />
                  <h3 className="font-display font-bold text-xl mb-2">Need Urgent Help?</h3>
                  <p className="text-sm text-white/80 leading-relaxed mb-4">
                    For time-sensitive matters such as payment issues or application deadlines, call our helpdesk directly during working hours.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gold-300" /> {SCHOOL.phone}</div>
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gold-300" /> {SCHOOL.email}</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <Building2 className="w-10 h-10 text-brand-700 dark:text-brand-400 mb-3" />
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">Visit the Campus</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed mb-3">
                    {SCHOOL.fullName} is located in Kano Ghari, along the Kano–Katsina road. Visitors are welcome during office hours; please bring valid identification.
                  </p>
                  <Badge variant="success">Open to Visitors</Badge>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <h3 className="font-display font-bold text-base text-ink-900 dark:text-ink-100 mb-3">Quick Links</h3>
                  <div className="space-y-2 text-sm">
                    <a href="/faq" className="block text-brand-700 dark:text-brand-400 hover:underline">Frequently Asked Questions</a>
                    <a href="/admission-requirements" className="block text-brand-700 dark:text-brand-400 hover:underline">Admission Requirements</a>
                    <a href="/fees" className="block text-brand-700 dark:text-brand-400 hover:underline">Fee Structure</a>
                    <a href="/downloads" className="block text-brand-700 dark:text-brand-400 hover:underline">Downloads & Resources</a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <Badge variant="warning" className="mb-3">Find Us</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Our Location</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              The College is situated in Kano Ghari, Bichi Local Government Area, Kano State — easily accessible by road from Kano metropolis.
            </p>
          </div>
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="aspect-[16/9] w-full bg-ink-100 dark:bg-ink-800">
              <iframe
                title="YMS COE Location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.0%2C12.0%2C8.5%2C12.5&layer=mapnik&marker=12.25%2C8.25"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
            <CardContent className="pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                <div>
                  <div className="font-semibold text-ink-900 dark:text-ink-100 text-sm">{SCHOOL.location}</div>
                  <div className="text-xs text-ink-500 dark:text-ink-400">{SCHOOL.address}</div>
                </div>
              </div>
              <a
                href="https://www.openstreetmap.org/?mlat=12.25&mlon=8.25#map=13/12.25/8.25"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-brand-700 dark:text-brand-400 hover:underline"
              >
                Open in Maps →
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </SiteShell>
  )
}
